import { Badge, Banner, Button, Dialog, Field, Input } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { SocialIcon, type SocialProvider } from '../components/SocialIcon';
import { DangerButton } from '../components/ui/DangerButton';
import { PasswordField } from '../components/ui/PasswordField';
import { ICONS } from '../components/ui/icons';

interface Security {
  mfa: { enabled: boolean; enabledAt: string | null; recoveryCodesLeft: number };
  passkeys: Array<{ id: string; name: string; createdAt: string; lastUsedAt: string | null; synced: boolean }>;
  identities: Array<{ id: string; provider: SocialProvider; label: string; email: string | null; createdAt: string; lastUsedAt: string | null }>;
  providers: Record<SocialProvider, boolean>;
  requiredBy: string[];
}

const LABELS: Record<SocialProvider, string> = { google: 'Google', microsoft: 'Microsoft', github: 'GitHub' };
const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
const date = (v: string | null) => (v ? new Date(v).toLocaleDateString('pt-BR') : 'nunca');

/** Minha conta: senha, verificação em duas etapas, passkeys e contas vinculadas */
export function AccountPage({ embedded = false, section }: { embedded?: boolean; section?: 'senha' | 'mfa' | 'contas' } = {}) {
  const { user } = useAuth();
  const { reloadContext } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [data, setData] = useState<Security | null>(null);
  const [notice, setNotice] = useState<{ variant: 'success' | 'danger'; text: string } | null>(null);

  // Senha
  const [pwd, setPwd] = useState({ atual: '', nova: '' });
  const [pwdBusy, setPwdBusy] = useState(false);

  // 2FA
  const [setup, setSetup] = useState<{ qrDataUrl: string; secret: string; setupToken: string } | null>(null);
  const [setupCode, setSetupCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableForm, setDisableForm] = useState({ senha: '', code: '' });
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenCode, setRegenCode] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Passkeys e contas vinculadas
  const [passkeyName, setPasskeyName] = useState('');
  const [passkeyOpen, setPasskeyOpen] = useState(false);
  const [removePasskey, setRemovePasskey] = useState<Security['passkeys'][number] | null>(null);
  const [unlink, setUnlink] = useState<Security['identities'][number] | null>(null);

  const load = useCallback(async () => {
    const res = await fetchApi('/api/account/security');
    if (res.ok) setData(await res.json());
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  // Volta de "vincular conta" (login social)
  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    if (hash.get('linked')) setNotice({ variant: 'success', text: `Conta ${LABELS[hash.get('linked') as SocialProvider] || ''} vinculada. Você já pode entrar com ela.` });
    if (hash.get('error')) setNotice({ variant: 'danger', text: hash.get('error')! });
    if ([...hash.keys()].length) window.history.replaceState(null, '', window.location.pathname);
  }, []);

  const call = async (key: string, url: string, method: string, body?: unknown) => {
    setBusy(key);
    setError(null);
    try {
      const res = await fetchApi(url, { method, body: body === undefined ? undefined : JSON.stringify(body) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Não foi possível concluir');
      return json;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível concluir');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!PASSWORD_RULE.test(pwd.nova)) return toast.error('A nova senha precisa de 8 caracteres ou mais, com maiúscula, minúscula e número');
    setPwdBusy(true);
    try {
      const res = await fetchApi('/api/account/password', { method: 'PUT', body: JSON.stringify(pwd) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Erro ao alterar a senha');
      toast.success('Senha alterada');
      setPwd({ atual: '', nova: '' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao alterar a senha');
    } finally {
      setPwdBusy(false);
    }
  };

  const startMfa = async () => {
    const json = await call('mfa-setup', '/api/account/mfa/setup', 'POST', {});
    if (json) { setSetup(json); setSetupCode(''); }
  };

  const confirmMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setup) return;
    const json = await call('mfa-enable', '/api/account/mfa/enable', 'POST', { setupToken: setup.setupToken, code: setupCode });
    if (json) { setSetup(null); setCodes(json.recoveryCodes); load(); reloadContext(); }
  };

  const disableMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    const json = await call('mfa-disable', '/api/account/mfa/disable', 'POST', disableForm);
    if (json) { setDisableOpen(false); setDisableForm({ senha: '', code: '' }); toast.success('Verificação em duas etapas desativada'); load(); }
  };

  const regenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    const json = await call('regen', '/api/account/mfa/recovery-codes', 'POST', { code: regenCode });
    if (json) { setRegenOpen(false); setRegenCode(''); setCodes(json.recoveryCodes); load(); }
  };

  const addPasskey = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy('passkey');
    setError(null);
    try {
      const optRes = await fetchApi('/api/account/passkeys/options', { method: 'POST', body: '{}' });
      const opt = await optRes.json();
      if (!optRes.ok) throw new Error(opt.message || 'Erro ao preparar a passkey');
      const response = await startRegistration({ optionsJSON: opt.options });
      const res = await fetchApi('/api/account/passkeys', {
        method: 'POST',
        body: JSON.stringify({ challengeToken: opt.challengeToken, response, name: passkeyName || 'Passkey' }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Erro ao cadastrar a passkey');
      toast.success('Passkey cadastrada');
      setPasskeyOpen(false);
      setPasskeyName('');
      load();
      reloadContext();
    } catch (err) {
      const name = (err as { name?: string })?.name;
      if (name === 'InvalidStateError') setError('Este dispositivo já tem uma passkey desta conta');
      else if (name !== 'NotAllowedError' && name !== 'AbortError') setError(err instanceof Error ? err.message : 'Erro ao cadastrar a passkey');
    } finally {
      setBusy(null);
    }
  };

  const confirmRemovePasskey = async () => {
    const p = removePasskey;
    setRemovePasskey(null);
    if (!p) return;
    const json = await call('rm-passkey', `/api/account/passkeys/${encodeURIComponent(p.id)}`, 'DELETE');
    if (json) { toast.success('Passkey removida'); load(); } else if (error) toast.error(error);
  };

  const link = async (provider: SocialProvider) => {
    const json = await call(`link-${provider}`, `/api/account/identities/${provider}/link`, 'POST', {});
    if (json?.url) window.location.assign(json.url);
  };

  const confirmUnlink = async () => {
    const i = unlink;
    setUnlink(null);
    if (!i) return;
    const json = await call('unlink', `/api/account/identities/${i.id}`, 'DELETE');
    if (json) { toast.success(`Conta ${i.label} desvinculada`); load(); }
  };

  const copyCodes = async () => {
    if (!codes) return;
    try { await navigator.clipboard.writeText(codes.join('\n')); toast.success('Códigos copiados'); } catch { /* sem permissão */ }
  };

  const downloadCodes = () => {
    if (!codes) return;
    const blob = new Blob([`Códigos de recuperação — ${user?.email}\nCada código vale uma vez.\n\n${codes.join('\n')}\n`], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'codigos-de-recuperacao.txt';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const unprotected = data && !data.mfa.enabled && data.passkeys.length === 0;
  const show = (k: 'senha' | 'mfa' | 'contas') => !section || section === k;
  const linkable = data ? (Object.keys(data.providers) as SocialProvider[]).filter((p) => data.providers[p] && !data.identities.some((i) => i.provider === p)) : [];

  return (
    <>
      {!embedded && (
        <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Minha conta</h2>
          <div style={{ color: 'var(--text-muted)' }}>{user?.nome} · {user?.email}</div>
        </div>
      )}

      <div className={embedded ? 'space-y-4' : 'page-content space-y-6'} style={embedded ? undefined : { maxWidth: 880 }}>
        {notice && (
          <Banner variant={notice.variant === 'danger' ? 'error' : 'default'} description={<>
            {notice.text}
          </>} />
        )}
        {data && data.requiredBy.length > 0 && unprotected && (
          <Banner variant="alert" description={<>
            {data.requiredBy.join(', ')} exige verificação em duas etapas. Configure o aplicativo autenticador ou cadastre uma passkey para continuar usando a plataforma.
          </>} />
        )}

        {/* Senha */}
        {show('senha') && <section className="brand-card">
          <h4 className="text-base font-semibold">Senha</h4>
          <div style={{ color: 'var(--text-muted)', marginTop: 2, marginBottom: 16 }}>8 caracteres ou mais, com letra maiúscula, minúscula e número.</div>
          <form onSubmit={changePassword} className="grid gap-4 md:grid-cols-2">
            <PasswordField label="Senha atual" autoComplete="current-password" value={pwd.atual} onChange={(v) => setPwd({ ...pwd, atual: v })} />
            <PasswordField label="Nova senha" autoComplete="new-password" value={pwd.nova} onChange={(v) => setPwd({ ...pwd, nova: v })} />
            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" variant="primary" loading={pwdBusy} disabled={!pwd.atual || !pwd.nova}>Alterar senha</Button>
            </div>
          </form>
        </section>}

        {/* 2FA */}
        {show('mfa') && <section className="brand-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-base font-semibold">Verificação em duas etapas</h4>
                {data && (data.mfa.enabled ? <Badge variant="green">Ativa</Badge> : <Badge variant="neutral">Desativada</Badge>)}
              </div>
              <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>
                Além da senha, um código do aplicativo autenticador (Google Authenticator, Microsoft Authenticator, 1Password…).
              </div>
              {data?.mfa.enabled && (
                <div style={{ color: 'var(--text-muted)', marginTop: 8, fontSize: 13 }}>
                  Ativa desde {date(data.mfa.enabledAt)} · {data.mfa.recoveryCodesLeft} códigos de recuperação restantes
                </div>
              )}
            </div>
            {data && !data.mfa.enabled && (
              <Button variant="primary" style={{ flexShrink: 0 }} loading={busy === 'mfa-setup'} onClick={startMfa}>Configurar</Button>
            )}
          </div>
          {data?.mfa.enabled && (
            <div className="flex gap-2" style={{ marginTop: 16 }}>
              <Button variant="secondary" onClick={() => { setError(null); setRegenOpen(true); }}>Gerar novos códigos de recuperação</Button>
              <DangerButton variant="dangerOutline" onClick={() => { setError(null); setDisableOpen(true); }}>Desativar</DangerButton>
            </div>
          )}
        </section>}

        {/* Passkeys */}
        {show('mfa') && <section className="brand-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-base font-semibold">Passkeys</h4>
              <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>
                Entre com a digital, o rosto ou o PIN do dispositivo, sem senha. Vale como verificação em duas etapas.
              </div>
            </div>
            <Button variant="secondary" icon={ICONS.Plus} style={{ flexShrink: 0 }} disabled={!browserSupportsWebAuthn()} onClick={() => { setError(null); setPasskeyOpen(true); }}>
              Adicionar passkey
            </Button>
          </div>
          {data && data.passkeys.length > 0 && (
            <ul className="account-list">
              {data.passkeys.map((p) => (
                <li key={p.id}>
                  <ICONS.Key size={16} />
                  <div>
                    <strong>{p.name}</strong>
                    <span>Criada em {date(p.createdAt)} · último uso {date(p.lastUsedAt)}{p.synced ? ' · sincronizada' : ''}</span>
                  </div>
                  <Button variant="ghost" shape="square" icon={ICONS.Trash} aria-label={`Remover ${p.name}`} onClick={() => setRemovePasskey(p)} />
                </li>
              ))}
            </ul>
          )}
        </section>}

        {/* Contas vinculadas */}
        {show('contas') && data && (data.identities.length > 0 || linkable.length > 0) && (
          <section className="brand-card">
            <h4 className="text-base font-semibold">Contas vinculadas</h4>
            <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>Entre com uma conta Google, Microsoft ou GitHub.</div>
            <ul className="account-list">
              {data.identities.map((i) => (
                <li key={i.id}>
                  <SocialIcon provider={i.provider} size={20} />
                  <div>
                    <strong>{i.label}</strong>
                    <span>{i.email || 'sem e-mail'} · último uso {date(i.lastUsedAt)}</span>
                  </div>
                  <Button variant="secondary" onClick={() => setUnlink(i)}>Desvincular</Button>
                </li>
              ))}
              {linkable.map((p) => (
                <li key={p}>
                  <SocialIcon provider={p} size={20} />
                  <div><strong>{LABELS[p]}</strong><span>Não vinculada</span></div>
                  <Button variant="secondary" loading={busy === `link-${p}`} onClick={() => link(p)}>Vincular</Button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* Configurar 2FA */}
      <Dialog.Root open={setup !== null} onOpenChange={(o: boolean) => { if (!o) (() => setSetup(null))(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Configurar o aplicativo autenticador</h3>}</Dialog.Title>

        <form onSubmit={confirmMfa}>
          <div style={{ color: 'var(--text-muted)', marginBottom: 16 }}>1. Escaneie o QR Code no aplicativo. 2. Digite o código de 6 dígitos que aparecer.</div>
          {error && <div className="mb-4"><Banner variant="error" description={<>{error}</>} /></div>}
          {setup && (
            <div className="mfa-setup">
              <img src={setup.qrDataUrl} alt="QR Code para o aplicativo autenticador" width={180} height={180} />
              <div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Não consegue escanear? Digite esta chave:</div>
                <code className="mfa-secret">{setup.secret.match(/.{1,4}/g)?.join(' ')}</code>
                <Field label="Código">
                  <Input onChange={(e) => setSetupCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" placeholder="000000" value={setupCode} autoFocus />
                </Field>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2" style={{ marginTop: 24 }}>
            <Button variant="secondary" type="button" onClick={() => setSetup(null)}>Cancelar</Button>
            <Button type="submit" variant="primary" loading={busy === 'mfa-enable'} disabled={setupCode.trim().length < 6}>Ativar</Button>
          </div>
        </form>
      
</Dialog>
</Dialog.Root>

      {/* Códigos de recuperação */}
      <Dialog.Root open={codes !== null} onOpenChange={(o: boolean) => { if (!o) (() => undefined)(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Guarde os códigos de recuperação</h3>}</Dialog.Title>

        <div style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
          Se perder o celular, entre com um destes códigos. Cada um vale uma vez. Eles não serão mostrados de novo.
        </div>
        <ul className="recovery-codes">{codes?.map((c) => <li key={c}>{c}</li>)}</ul>
        <div className="flex justify-between gap-2" style={{ marginTop: 24 }}>
          <div className="flex gap-2">
            <Button variant="secondary" icon={ICONS.Copy} onClick={copyCodes}>Copiar</Button>
            <Button variant="secondary" icon={ICONS.Download} onClick={downloadCodes}>Baixar</Button>
          </div>
          <Button variant="primary" onClick={() => setCodes(null)}>Já guardei</Button>
        </div>
      
</Dialog>
</Dialog.Root>

      {/* Desativar 2FA */}
      <Dialog.Root open={disableOpen} onOpenChange={(o: boolean) => { if (!o) (() => setDisableOpen(false))(); }}>
<Dialog size="sm" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Desativar a verificação em duas etapas</h3>}</Dialog.Title>

        <form onSubmit={disableMfa} className="flex flex-col gap-4">
          {error && <Banner variant="error" description={<>{error}</>} />}
          <PasswordField label="Senha" autoComplete="current-password" value={disableForm.senha} onChange={(v) => setDisableForm({ ...disableForm, senha: v })} />
          <Field label="Código do aplicativo ou de recuperação">
            <Input onChange={(e) => setDisableForm({ ...disableForm, code: e.target.value })} value={disableForm.code} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setDisableOpen(false)}>Cancelar</Button>
            <DangerButton loading={busy === 'mfa-disable'} disabled={!disableForm.senha || !disableForm.code}>Desativar</DangerButton>
          </div>
        </form>
      
</Dialog>
</Dialog.Root>

      {/* Novos códigos */}
      <Dialog.Root open={regenOpen} onOpenChange={(o: boolean) => { if (!o) (() => setRegenOpen(false))(); }}>
<Dialog size="sm" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Gerar novos códigos</h3>}</Dialog.Title>

        <form onSubmit={regenerate} className="flex flex-col gap-4">
          <div style={{ color: 'var(--text-muted)' }}>Os códigos antigos deixam de valer.</div>
          {error && <Banner variant="error" description={<>{error}</>} />}
          <Field label="Código do aplicativo">
            <Input onChange={(e) => setRegenCode(e.target.value)} inputMode="numeric" value={regenCode} autoFocus />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setRegenOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" loading={busy === 'regen'} disabled={regenCode.trim().length < 6}>Gerar</Button>
          </div>
        </form>
      
</Dialog>
</Dialog.Root>

      {/* Nova passkey */}
      <Dialog.Root open={passkeyOpen} onOpenChange={(o: boolean) => { if (!o) (() => setPasskeyOpen(false))(); }}>
<Dialog size="sm" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Adicionar passkey</h3>}</Dialog.Title>

        <form onSubmit={addPasskey} className="flex flex-col gap-4">
          <div style={{ color: 'var(--text-muted)' }}>O navegador vai pedir a digital, o rosto ou o PIN deste dispositivo.</div>
          {error && <Banner variant="error" description={<>{error}</>} />}
          <Field label="Nome">
            <Input onChange={(e) => setPasskeyName(e.target.value)} placeholder="Ex.: Notebook do trabalho" value={passkeyName} autoFocus />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setPasskeyOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" loading={busy === 'passkey'}>Continuar</Button>
          </div>
        </form>
      
</Dialog>
</Dialog.Root>

      <DangerConfirmModal open={removePasskey !== null} title="Remover passkey" confirmText="Remover" onConfirm={confirmRemovePasskey} onCancel={() => setRemovePasskey(null)}>
        A passkey <strong>{removePasskey?.name}</strong> deixa de entrar nesta conta.
      </DangerConfirmModal>
      <DangerConfirmModal open={unlink !== null} title="Desvincular conta" confirmText="Desvincular" onConfirm={confirmUnlink} onCancel={() => setUnlink(null)}>
        Você deixa de entrar com a conta {unlink?.label}{unlink?.email ? ` (${unlink.email})` : ''}.
      </DangerConfirmModal>
    </>
  );
}
